import { useTheme } from '@mui/material/styles';
import { FONTS } from '../../../config/app-config';
import React, { useState, useId, useEffect } from 'react';
import {
    ListItem,
    ListItemText,
    Box,
    Checkbox,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { SIDENAV_MODES } from "../SideNavigation";

/**
 * Renders a single TOC item with optional checkbox for selectionMode,
 * and allows manual expand/collapse.
 */
export const TocItem = ({
    item,
    level = 0,
    defaultOpenLevels,
    onArticleSelect,
    selectedArticleId,
    activePath,
    mode,
    selectedIds,
    toggleSelect,
    TocList,
    autoScrollEnabled
}) => {
    const theme = useTheme();
    // Resolve the entire active path in one render, including newly mounted branches.
    // A manual toggle lasts until navigation selects another article.
    const [manualExpansion, setManualExpansion] = useState(null);
    const expanded = manualExpansion && manualExpansion.articleId === selectedArticleId
        ? manualExpansion.expanded
        : selectedArticleId ? activePath.includes(item._id) : level < defaultOpenLevels;
    if (manualExpansion && manualExpansion.articleId !== selectedArticleId) setManualExpansion(null);
    const childrenId = useId();
    const [hasOpened, setHasOpened] = useState(expanded);
    if (expanded && !hasOpened) setHasOpened(true);
    useEffect(() => {
        if (expanded || !hasOpened) return undefined;
        // Retain children only for the closing animation, not every visited branch.
        const timer = setTimeout(() => setHasOpened(false), 200);
        return () => clearTimeout(timer);
    }, [expanded, hasOpened]);

    // 2) Collect all IDs in the subtree for selection behavior
    const descendantIds = React.useMemo(() => {
        const all = [];
        if (mode === SIDENAV_MODES.none) return all;
        function walk(node) {
            all.push(node._id);
            if (node.children) node.children.forEach(walk);
        }
        walk(item);
        return all;
    }, [item, mode]);

    // 3) Determine checkbox states
    const allSelected = descendantIds.every(id => selectedIds.includes(id));
    const someSelected = descendantIds.some(id => selectedIds.includes(id));
    const indeterminate = !allSelected && someSelected;

    const handleItemClick = () => {
        if (mode === SIDENAV_MODES.none && onArticleSelect) {
            onArticleSelect(item._id);
        }
    };

    const handleCheckboxClick = e => {
        e.stopPropagation();
        toggleSelect(item._id); // nur Auswahl toggeln
    };

    // Titel-Klick lädt IMMER den Inhalt (auch im Selection-Mode)
    const handleTitleClick = (e) => {
        e.stopPropagation();
        if (onArticleSelect) onArticleSelect(item._id);
    };

    const isActive = item._id === selectedArticleId;

    return (
        <>
            <ListItem
                button={(mode === SIDENAV_MODES.none).toString()}
                data-id={item._id}
                onClick={handleItemClick}
                sx={{
                    pl: 2 * level,
                    py: 0.25,
                    backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
                    color: isActive ? 'white' : 'inherit',
                    cursor: mode !== SIDENAV_MODES.none ? 'default' : 'pointer',
                    '&:hover': {
                        backgroundColor: isActive
                            ? theme.palette.primary.main
                            : (mode !== SIDENAV_MODES.none ? 'transparent' : 'rgba(0, 0, 0, 0.04)'),
                    },
                }}
            >
                {mode !== SIDENAV_MODES.none && (
                    <Checkbox
                        checked={allSelected}
                        indeterminate={indeterminate}
                        onClick={handleCheckboxClick}
                        sx={{
                            ml: 2,
                            color: isActive ? 'white !important' : 'inherit !important',
                            fill: isActive ? 'white' : 'inherit',
                        }}
                    />
                )}

                <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                        onClick: handleTitleClick, // Titel öffnet Inhalt
                        sx: {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            cursor: 'pointer',
                        }
                    }}
                    sx={{ fontFamily: FONTS.body, ml: mode !== SIDENAV_MODES.none ? 1 : 2 }}
                />

                {/* Expand/collapse toggle — auch im Selection-Mode sichtbar */}
                {item.children?.length > 0 && (
                    <IconButton
                        onClick={e => {
                            e.stopPropagation();
                            setManualExpansion({ articleId: selectedArticleId, expanded: !expanded });
                        }}
                        size="small"
                        color="inherit"
                        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.title}`}
                        aria-expanded={expanded}
                        aria-controls={childrenId}
                    >
                        <ExpandMoreIcon sx={{
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        }} />
                    </IconButton>
                )}
            </ListItem>

            {/* Keep children through the closing animation, then release their DOM. */}
            {item.children?.length > 0 && (
                <Box
                    id={childrenId}
                    aria-hidden={!expanded}
                    inert={!expanded ? true : undefined}
                    sx={{
                        display: 'grid',
                        gridTemplateRows: expanded ? '1fr' : '0fr',
                        // Respect hidden ancestors, including the closed mobile drawer.
                        visibility: expanded ? 'inherit' : 'hidden',
                        transition: 'grid-template-rows 200ms cubic-bezier(0.2, 0, 0, 1), visibility 200ms',
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                >
                    {/* Intrinsic sizing avoids stale pixel heights when nested branches change. */}
                    <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
                        {(expanded || hasOpened) && <TocList
                            items={item.children}
                            level={level + 1}
                            defaultOpenLevels={defaultOpenLevels}
                            onArticleSelect={onArticleSelect}
                            selectedArticleId={selectedArticleId}
                            activePath={activePath}
                            mode={mode}
                            selectedIds={selectedIds}
                            toggleSelect={toggleSelect}
                        />}
                    </Box>
                </Box>
            )}
        </>
    );
};

